<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Multi-User Access') }}
    </div>
    <div class="settings-content">
      <div v-if="loading" class="loading-overlay">
        <LoadingCircle width="32" height="32" layout="inline" />
      </div>
      <ui-tabs
        v-else
        @switchTab="tabMultiUserAccess = $event"
        styleType="style-list"
        :tabs="tabs"
      >
        <template #content-workspaces>
          <workspaces
            v-if="canAccessPage('profile.multi-user-access:workspaces')"
            :workspaces-data="workspacesData"
            :workspaces-data-normalized="workspacesDataNormalized"
            :private-workspaces-data="privateWorkspacesData"
            :private-workspaces-data-normalized="privateWorkspacesDataNormalized"
            :refresh-workspaces="refreshWorkspaces"
            :apply-workspace="applyWorkspace"
            :members-data="membersData"
            :members-data-normalized="membersDataNormalized"
            :members-loading="membersLoading"
            :refresh-members="refreshMembers"
          />
        </template>
        <template #content-usersManagement>
          <user-management
            v-if="canAccessPage('profile.multi-user-access:user-management')"
            :private-workspaces-data="privateWorkspacesData"
            :private-workspaces-data-normalized="privateWorkspacesDataNormalized"
            :refresh-workspaces="refreshWorkspaces"
            :members-data="membersData"
            :members-data-normalized="membersDataNormalized"
            :members-loading="membersLoading"
            :refresh-members="refreshMembers"
          />
        </template>
        <template #content-sharedReports>
          <shared-reports
            v-if="canAccessPage('profile.multi-user-access:shared-reports')"
            :isTabActive="tabMultiUserAccess === 'sharedReports'"
          />
        </template>
      </ui-tabs>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import UserManagement from './user-manager/user-management.vue';
import Workspaces from './workspaces/workspaces.vue';
import SharedReports from './shared-reports/shared-reports.vue';
import _ from 'lodash';
import { getUserWorkspacesApiRequest } from '@/services/api/workspaces-api';
import { getMembersApiRequest } from '@/services/api/user-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import LoadingCircle from '@/components/loading-circle.vue';
import { useAcl, useI18n } from '@/composables';
import { useStore } from '@/store';
import type { UserWorkspace } from '@/services/models';
const route = useRoute();
const { t } = useI18n();
const { canAccessPage } = useAcl();
const store = useStore();
const tabMultiUserAccess = ref('');

const loading = ref(true);
const workspacesData = ref<UserWorkspace[]>([]);
const workspacesDataNormalized = ref<Record<string, UserWorkspace>>({});
const privateWorkspacesData = ref<UserWorkspace[]>([]);
const privateWorkspacesDataNormalized = ref<Record<string, UserWorkspace>>({});
const membersData = ref<unknown[]>([]);
const membersDataNormalized = ref<Record<string, unknown>>({});
const membersLoading = ref(false);

const applyWorkspacesPayload = (payload: UserWorkspace[]) => {
  const normalized: Record<string, UserWorkspace> = {};
  payload.forEach((workspace: UserWorkspace) => {
    normalized[workspace.id] = workspace;
  });
  workspacesData.value = payload;
  workspacesDataNormalized.value = normalized;

  const privateWorkspaces = payload.filter((workspace) => workspace.type === 'private');
  const privateWorkspacesNormalized: Record<string, UserWorkspace> = {};
  privateWorkspaces.forEach((workspace) => {
    privateWorkspacesNormalized[workspace.id] = workspace;
  });
  privateWorkspacesData.value = _.orderBy(privateWorkspaces, ['deletable', 'name'], ['asc', 'asc']);
  privateWorkspacesDataNormalized.value = privateWorkspacesNormalized;
};

const refreshWorkspaces = async () => {
  const payload = await getUserWorkspacesApiRequest();
  applyWorkspacesPayload(payload);
};

const applyWorkspace = (workspace: UserWorkspace) => {
  const normalized = {
    ...workspacesDataNormalized.value,
    [workspace.id]: workspace,
  };
  workspacesDataNormalized.value = normalized;
  workspacesData.value = _.orderBy(Object.values(normalized), ['deletable', 'name'], ['asc', 'asc']);

  if (workspace.type !== 'private') return;

  const privateNormalized = {
    ...privateWorkspacesDataNormalized.value,
    [workspace.id]: workspace,
  };
  privateWorkspacesDataNormalized.value = privateNormalized;
  privateWorkspacesData.value = _.orderBy(
    Object.values(privateNormalized),
    ['deletable', 'name'],
    ['asc', 'asc'],
  );
};

const applyMembersPayload = (payload: unknown[]) => {
  const normalized: Record<string, unknown> = {};
  payload.forEach((member: any) => {
    member.fullName = (member.firstName || '') + (member.lastName ? ` ${member.lastName}` : '');
    normalized[member.id] = member;
  });
  membersData.value = payload;
  membersDataNormalized.value = normalized;
};

const refreshMembers = async () => {
  membersLoading.value = true;
  try {
    const payload = await getMembersApiRequest();
    applyMembersPayload(payload);
    return payload;
  } finally {
    membersLoading.value = false;
  }
};

const tabs = computed(() => {
  const result = [];
  if (canAccessPage('profile.multi-user-access:workspaces')) {
    result.push({
      label: t('Workspaces'),
      key: 'workspaces',
    });
  }
  if (canAccessPage('profile.multi-user-access:user-management')) {
    result.push({
      label: t('Users management'),
      key: 'usersManagement',
    });
  }
  if (canAccessPage('profile.multi-user-access:shared-reports')) {
    result.push({
      label: t('Shared reports'),
      key: 'sharedReports',
    });
  }

  return result;
});

watch(
  tabs,
  (tabItems) => {
    if (!tabMultiUserAccess.value && tabItems.length > 0) {
      tabMultiUserAccess.value = tabItems[0].key;
    }
  },
  { immediate: true },
);

const load = async () => {
  const promises: Promise<unknown>[] = [];
  const canAccessWorkspaces = canAccessPage('profile.multi-user-access:workspaces');
  const canAccessUserManagement = canAccessPage('profile.multi-user-access:user-management');

  if (canAccessWorkspaces) {
    promises.push(refreshUserProfileFromApi());
    promises.push(
      getUserWorkspacesApiRequest().then((payload: UserWorkspace[]) => {
        applyWorkspacesPayload(payload);
      }),
    );
  }
  if (canAccessWorkspaces || canAccessUserManagement) {
    promises.push(refreshMembers());
  }
  await Promise.all(promises);
};

onMounted(async () => {
  store.setLoading(true);
  try {
    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }
    await load();
    loading.value = false;
  } finally {
    store.setLoading(false);
  }
});
</script>

<style lang="scss" scoped>

.loading-overlay {
  width: 100%;
  display: flex;
  height: 200px;
  align-items: center;
  justify-content: center;
}
</style>
